import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  ClockIcon,
  CloseIcon,
  DescriptionIcon,
  Divider,
  EditIcon,
  EmptyState,
  Filter,
  FilterGroup,
  FilterIcon,
  Pagination,
  PlusIcon,
  ProgressIcon,
  Search,
  SendIcon,
  SuccessIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useDocumentTitle,
} from '@forge/common';
import type {
  FilterOptionCheckboxValue,
  FilterOptionDateRangeValue,
  FilterOptionNumberRangeValue,
} from '@forge/common';
import { PRIORITY_META, STATUS_META } from '@/data/mock-requirements';
import type {
  RequirementPriority,
  RequirementStatus,
} from '@/data/mock-requirements';
import { useIntakeStore } from './intakeStore';

const PAGE_SIZE = 10;

const statusIcons: Record<RequirementStatus, JSX.Element> = {
  draft: <EditIcon />,
  submitted: <SendIcon />,
  in_account_manager: <ClockIcon />,
  in_development: <ProgressIcon />,
  fulfilled: <SuccessIcon />,
  cancelled: <CloseIcon />,
};

const formatUpdatedDate = (iso: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));

const IntakeListPage = () => {
  useDocumentTitle('Intake — OneRing Portal');
  const navigate = useNavigate();
  const { requirements } = useIntakeStore();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<RequirementStatus | 'all'>('all');
  const [priorities, setPriorities] = useState<RequirementPriority[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [updatedFrom, setUpdatedFrom] = useState<string | undefined>(undefined);
  const [updatedTo, setUpdatedTo] = useState<string | undefined>(undefined);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const statusCounts = useMemo(
    () =>
      requirements.reduce<Record<RequirementStatus, number>>(
        (counts, requirement) => ({
          ...counts,
          [requirement.status]: counts[requirement.status] + 1,
        }),
        {
          draft: 0,
          submitted: 0,
          in_account_manager: 0,
          in_development: 0,
          fulfilled: 0,
          cancelled: 0,
        },
      ),
    [requirements],
  );

  const clientOptions = useMemo(
    () =>
      [...new Set(requirements.map((requirement) => requirement.clientCompany))]
        .sort((left, right) => left.localeCompare(right))
        .map((client) => ({ label: client, value: client })),
    [requirements],
  );

  const filteredRequirements = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = updatedFrom ? new Date(`${updatedFrom}T00:00:00`).getTime() : undefined;
    const to = updatedTo ? new Date(`${updatedTo}T23:59:59`).getTime() : undefined;

    return requirements.filter((requirement) => {
      if (status !== 'all' && requirement.status !== status) return false;
      if (priorities.length > 0 && !priorities.includes(requirement.priority)) {
        return false;
      }
      if (clients.length > 0 && !clients.includes(requirement.clientCompany)) {
        return false;
      }

      const updatedAt = new Date(requirement.updatedAt).getTime();
      if (from !== undefined && updatedAt < from) return false;
      if (to !== undefined && updatedAt > to) return false;

      if (
        query &&
        ![
          requirement.reference,
          requirement.title,
          requirement.clientName,
          requirement.clientCompany,
        ].some((value) => value.toLowerCase().includes(query))
      ) {
        return false;
      }
      return true;
    });
  }, [clients, priorities, requirements, search, status, updatedFrom, updatedTo]);

  const totalPages = Math.max(1, Math.ceil(filteredRequirements.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleRequirements = filteredRequirements.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const activeFilterCount =
    priorities.length + clients.length + (updatedFrom || updatedTo ? 1 : 0);

  const handleFilterChange = (
    values: Record<
      string,
      FilterOptionCheckboxValue[] | FilterOptionDateRangeValue | FilterOptionNumberRangeValue
    >,
  ) => {
    const selectedPriorities = (values['request-priority'] ??
      []) as FilterOptionCheckboxValue[];
    const selectedClients = (values['request-client'] ?? []) as FilterOptionCheckboxValue[];
    const updatedRange = values['request-updated'] as FilterOptionDateRangeValue | undefined;

    setPriorities(
      selectedPriorities.map((option) => String(option.value) as RequirementPriority),
    );
    setClients(selectedClients.map((option) => String(option.value)));
    setUpdatedFrom(updatedRange?.from);
    setUpdatedTo(updatedRange?.to);
    setFilterOpen(false);
    setPage(1);
  };

  const clearAdvancedFilters = () => {
    setPriorities([]);
    setClients([]);
    setUpdatedFrom(undefined);
    setUpdatedTo(undefined);
    setFilterOpen(false);
    setPage(1);
  };

  const statusFilters: Array<{
    key: RequirementStatus | 'all';
    label: string;
    count: number;
    icon: JSX.Element;
  }> = [
    {
      key: 'all',
      label: 'All Requests',
      count: requirements.length,
      icon: <DescriptionIcon />,
    },
    ...Object.entries(STATUS_META).map(([key, meta]) => ({
      key: key as RequirementStatus,
      label: meta.label,
      count: statusCounts[key as RequirementStatus],
      icon: statusIcons[key as RequirementStatus],
    })),
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="mb-1 text-xl font-bold">Intake</h2>
          <p className="text-sm text-color-tertiary">
            Review and manage platform requests from submission through delivery.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={() => navigate('/intake/new')}
        >
          New request
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="w-64 flex-shrink-0">
          <Card>
            <CardContent>
              <p className="mb-2 text-xs font-bold uppercase text-color-tertiary">
                Requests
              </p>
              <Divider />
              <div className="flex flex-col">
                {statusFilters.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => {
                      setStatus(filter.key);
                      setPage(1);
                    }}
                    className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm transition-colors ${
                      status === filter.key
                        ? 'bg-blue-50 font-medium text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="flex-shrink-0">{filter.icon}</span>
                    <span className="flex-1">{filter.label}</span>
                    <span className="text-xs tabular-nums text-color-tertiary">
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 flex-1">
          <Card>
            <CardHeader
              title={status === 'all' ? 'All Requests' : STATUS_META[status].label}
              subtitle={`${filteredRequirements.length} request${
                filteredRequirements.length !== 1 ? 's' : ''
              }`}
              slotRight={
                <div className="flex items-center justify-end gap-3">
                  <Search
                    id="intake-search"
                    placeholder="Search requests..."
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                  <Button
                    variant="tertiary"
                    icon={<FilterIcon />}
                    onClick={() => setFilterOpen(true)}
                  >
                    Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                  </Button>
                </div>
              }
            />
            <CardContent>
              {filterOpen && (
                <Filter
                  title="Filter Requests"
                  container="modal"
                  onApply={handleFilterChange}
                  onCancel={() => setFilterOpen(false)}
                  onClearAll={clearAdvancedFilters}
                >
                  <FilterGroup
                    id="request-priority"
                    label="Priority"
                    type="checkbox"
                    selected={priorities}
                    options={Object.entries(PRIORITY_META).map(([value, meta]) => ({
                      label: meta.label,
                      value,
                    }))}
                  />
                  <FilterGroup
                    id="request-client"
                    label="Client"
                    type="checkbox"
                    selected={clients}
                    options={clientOptions}
                  />
                  <FilterGroup
                    id="request-updated"
                    label="Updated Date"
                    type="date-range"
                    dateRangeFrom={updatedFrom}
                    dateRangeTo={updatedTo}
                    options={[]}
                  />
                </Filter>
              )}

              {visibleRequirements.length === 0 ? (
                <EmptyState
                  icon={<DescriptionIcon />}
                  description="No requests match the current filters."
                />
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Request</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRequirements.map((requirement) => (
                        <TableRow
                          key={requirement.id}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => navigate(`/intake/${requirement.id}`)}
                        >
                          <TableCell>
                            <span className="block font-medium">{requirement.title}</span>
                            <span className="font-mono text-xs text-color-tertiary">
                              {requirement.reference}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="block">{requirement.clientCompany}</span>
                            <span className="text-xs text-color-tertiary">
                              {requirement.clientName}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge color={STATUS_META[requirement.status].color}>
                              {STATUS_META[requirement.status].label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              color={PRIORITY_META[requirement.priority].color}
                              outline
                            >
                              {PRIORITY_META[requirement.priority].label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-color-secondary">
                              {formatUpdatedDate(requirement.updatedAt)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredRequirements.length > PAGE_SIZE && (
                    <Pagination
                      currentPage={currentPage}
                      totalItems={filteredRequirements.length}
                      totalPages={totalPages}
                      pageSize={PAGE_SIZE}
                      showPageInfo
                      onPageChange={setPage}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default IntakeListPage;
